import React, { createContext, useContext, useState, useEffect } from 'react';
import { ADDON_FEATURES, getAddOnLimit, canPurchaseMoreAddOns, getRemainingAddOnSlots } from '../config/tierConfig';
import { useUser } from './UserContext';

const AddOnContext = createContext();

export const useAddOns = () => {
  const context = useContext(AddOnContext);
  if (!context) {
    throw new Error('useAddOns must be used within an AddOnProvider');
  }
  return context;
};

export const AddOnProvider = ({ children }) => {
  // Get user tier for limit checking
  const { userTier } = useUser();
  
  // State for purchased add-ons (in real app, this would come from API/database)
  const [purchasedAddOns, setPurchasedAddOns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load purchased add-ons from localStorage on mount (simulate API call)
  useEffect(() => {
    const savedAddOns = localStorage.getItem('plugly_purchased_addons');
    if (savedAddOns) {
      try {
        setPurchasedAddOns(JSON.parse(savedAddOns));
      } catch (error) {
        console.error('Error loading purchased add-ons:', error);
      }
    }
  }, []);

  // Save to localStorage whenever purchased add-ons change
  useEffect(() => {
    localStorage.setItem('plugly_purchased_addons', JSON.stringify(purchasedAddOns));
  }, [purchasedAddOns]);

  // Purchase add-ons with tier limit checking
  const purchaseAddOns = async (addOnIds) => {
    setIsLoading(true);
    try {
      // Check tier limits before purchase
      const newAddOns = addOnIds.filter(id => !purchasedAddOns.includes(id));
      const futureCount = purchasedAddOns.length + newAddOns.length;
      
      if (!canPurchaseMoreAddOns(userTier, futureCount - 1)) {
        const limit = getAddOnLimit(userTier);
        const tierName = userTier === 'smb' ? 'SMB' : userTier === 'professional' ? 'Professional' : 'Enterprise';
        return { 
          success: false, 
          message: `${tierName} plan is limited to ${limit} add-ons. Upgrade to get more add-ons!`,
          limitReached: true
        };
      }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add new add-ons to purchased list
      setPurchasedAddOns(prev => [...prev, ...newAddOns]);
      
      return { success: true, message: 'Add-ons purchased successfully!' };
    } catch (error) {
      console.error('Purchase failed:', error);
      return { success: false, message: 'Purchase failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  // Remove add-on (for testing/admin purposes)
  const removeAddOn = (addOnId) => {
    setPurchasedAddOns(prev => prev.filter(id => id !== addOnId));
  };

  // Get purchased add-on details
  const getPurchasedAddOnDetails = () => {
    return ADDON_FEATURES.filter(addon => purchasedAddOns.includes(addon.id));
  };

  // Check if user has specific add-on
  const hasAddOn = (addOnId) => {
    return purchasedAddOns.includes(addOnId);
  };

  // Get add-ons by category
  const getPurchasedAddOnsByCategory = () => {
    const purchased = getPurchasedAddOnDetails();
    const categories = {};
    
    purchased.forEach(addon => {
      if (!categories[addon.category]) {
        categories[addon.category] = [];
      }
      categories[addon.category].push(addon);
    });
    
    return categories;
  };

  const value = {
    purchasedAddOns,
    isLoading,
    purchaseAddOns,
    removeAddOn,
    getPurchasedAddOnDetails,
    hasAddOn,
    getPurchasedAddOnsByCategory,
    // Tier limit functions
    getAddOnLimit: () => getAddOnLimit(userTier),
    canPurchaseMore: () => canPurchaseMoreAddOns(userTier, purchasedAddOns.length),
    getRemainingSlots: () => getRemainingAddOnSlots(userTier, purchasedAddOns.length)
  };

  return (
    <AddOnContext.Provider value={value}>
      {children}
    </AddOnContext.Provider>
  );
};
